// Matched against the parsed hostname — a substring check would accept
// youtube.com.evil.example (a YouTube player over a Visit button that goes
// somewhere else) and google.com/url?q=...youtube.com... redirect links.
const YOUTUBE_SITE_HOSTS = [
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "music.youtube.com",
];
const YOUTUBE_HOSTS = [...YOUTUBE_SITE_HOSTS, "youtu.be", "www.youtu.be"];

// http(s), no embedded credentials, and one of `hosts` — the same bar the other
// link-embed providers hold a URL to.
const parseYoutubeUrl = (videoUrl: string, hosts: string[]) => {
  try {
    const parsed = new URL(videoUrl);
    const isHttp = parsed.protocol === "https:" || parsed.protocol === "http:";
    const hasCredentials = Boolean(parsed.username || parsed.password);
    return isHttp && !hasCredentials && hosts.includes(parsed.hostname)
      ? parsed
      : undefined;
  } catch {
    return undefined;
  }
};

const isYoutubeHost = (videoUrl: string) =>
  Boolean(parseYoutubeUrl(videoUrl, YOUTUBE_HOSTS));

export const getYoutubeVideoId = (videoUrl: string) => {
  try {
    const videoUrlObject = new URL(videoUrl);
    const videoId = videoUrlObject.searchParams.get("v");
    if (!videoId) {
      return videoUrlObject.pathname.slice(1);
    }
    return videoId;
  } catch (e) {
    return undefined;
  }
};

export const isYoutubeVideoUrl = (videoUrl: string) => {
  if (!isYoutubeHost(videoUrl)) {
    return false;
  }
  if (videoUrl.includes("/c/")) {
    return false;
  }
  if (videoUrl.includes("/shorts/")) {
    return false;
  }
  if (videoUrl.includes("/live/")) {
    return false;
  }
  if (videoUrl.includes("/playlist")) {
    return false;
  }
  if (videoUrl.includes("/channel/")) {
    return false;
  }
  if (videoUrl.includes("/user/")) {
    return false;
  }
  if (videoUrl.includes("/@")) {
    return false;
  }
  if (videoUrl.includes("/search")) {
    return false;
  }
  if (videoUrl.includes("/results")) {
    return false;
  }
  if (videoUrl.includes("/about")) {
    return false;
  }
  if (videoUrl.includes("/community")) {
    return false;
  }
  if (videoUrl.includes("/creator")) {
    return false;
  }
  const videoId = getYoutubeVideoId(videoUrl);
  return Boolean(videoId);
};

// Shorts are ordinary YouTube videos behind a /shorts/<id> path, so they play in
// the regular embed player — but they're vertical, so callers lay them out 9:16
// rather than in the 16:9 slot `isYoutubeVideoUrl` links get. That's why this is
// a separate check instead of a loosening of `isYoutubeVideoUrl`.
export const getYoutubeShortsVideoId = (videoUrl: string) => {
  const parsed = parseYoutubeUrl(videoUrl, YOUTUBE_SITE_HOSTS);
  if (!parsed) return undefined;
  // Exactly /shorts/<id> — a trailing slash or query string is fine, further
  // path segments are some other page.
  const parts = parsed.pathname.split("/").filter(Boolean);
  if (parts.length !== 2 || parts[0] !== "shorts") return undefined;
  return /^[\w-]+$/.test(parts[1]) ? parts[1] : undefined;
};

export const isYoutubeShortsUrl = (videoUrl: string) =>
  Boolean(getYoutubeShortsVideoId(videoUrl));

// Anything the YouTube player can play inline — what feed cards key their play
// overlay (and autoplay-on-open) on.
export const isYoutubePlayableUrl = (videoUrl: string) =>
  isYoutubeVideoUrl(videoUrl) || isYoutubeShortsUrl(videoUrl);
